import { useEffect, useState, type ReactNode } from 'react';
import { useWindowHost } from '@/shared/lib/windows';
import { UnsavedChangesDialog } from './UnsavedChangesDialog';

export interface UseUnsavedCloseOptions {
  /** В окне есть несохранённые изменения. */
  dirty: boolean;
  /** Закрыть окно по-настоящему (без вопросов). */
  onClose: () => void;
  /** «Сохранить» в диалоге: сохранить и закрыть окно. */
  onSave: () => void;
  /** Подпись кнопки сохранения в диалоге: «Сохранить черновик» / «Сохранить». */
  saveLabel: string;
  saving?: boolean;
}

/**
 * Закрытие окна с вопросом о несохранённом (§8.7): `requestClose` — для `Esc`, крестика и кнопки
 * «Закрыть»; `dialog` — элемент диалога, его нужно вывести рядом с `FloatingWindow`. Заодно
 * сообщает менеджеру окон о несохранённых изменениях (точка на плашке свёрнутого окна) и просит
 * браузер подтвердить закрытие вкладки, пока они есть.
 */
export function useUnsavedClose({ dirty, onClose, onSave, saveLabel, saving }: UseUnsavedCloseOptions): { requestClose: () => void; dialog: ReactNode } {
  const host = useWindowHost();
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    host?.setDirty(dirty);
  }, [host, dirty]);

  useEffect(() => {
    if (!dirty) return;
    function handler(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const requestClose = () => {
    if (dirty) setAsking(true);
    else onClose();
  };

  const dialog = (
    <UnsavedChangesDialog
      open={asking}
      saveLabel={saveLabel}
      saving={saving}
      onSave={() => {
        setAsking(false);
        onSave();
      }}
      onDiscard={() => {
        setAsking(false);
        onClose();
      }}
      onCancel={() => setAsking(false)}
    />
  );

  return { requestClose, dialog };
}
